import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { Order, ShippingAddress, CartItem } from '../types';
import { createNotification } from './notificationService';

/* =========================================================
   CREATE ORDERS (1 ORDER PER SELLER)
========================================================= */
export const createOrder = async (
  buyerId: string,
  buyerName: string,
  buyerEmail: string,
  items: CartItem[],
  shippingAddress: ShippingAddress,
  totalAmount: number
): Promise<string[]> => {
  const orderIds: string[] = [];

  try {
    for (const item of items) {
      const orderData = {
        buyerId,
        buyerName,
        buyerEmail,
        sellerId: item.artwork.artistId,
        sellerName: item.artwork.artistName,
        artworkId: item.artwork.id,
        artworkTitle: item.artwork.title,
        artworkImage: item.artwork.imageUrl,
        quantity: item.quantity,
        price: item.artwork.price,
        totalAmount: item.artwork.price * item.quantity,
        status: 'pending',
        shippingAddress,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      orderIds.push(docRef.id);

      // Notify seller
      await createNotification(
        item.artwork.artistId,
        'purchase',
        'New Order Received!',
        `${buyerName} ordered "${item.artwork.title}"`,
        undefined,
        `/seller/orders/${docRef.id}`
      );
    }

    return orderIds;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

/* =========================================================
   GENERIC SINGLE ORDER CREATION (OPTIONAL UTILITY)
========================================================= */
export const createSingleOrder = async (
  orderData: Omit<Order, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'orders'), {
      ...orderData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating single order:', error);
    throw error;
  }
};

/* =========================================================
   BUYER ORDERS
========================================================= */
export const getBuyerOrders = async (buyerId: string): Promise<Order[]> => {
  try {
    const q = query(
      collection(db, 'orders'),
      where('buyerId', '==', buyerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(mapOrderDoc);
  } catch (error) {
    console.error('Error getting buyer orders:', error);
    return [];
  }
};

/* Alias for backward compatibility */
export const getOrdersByBuyer = getBuyerOrders;

/* =========================================================
   SELLER ORDERS
========================================================= */
export const getSellerOrders = async (sellerId: string): Promise<Order[]> => {
  try {
    const q = query(
      collection(db, 'orders'),
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(mapOrderDoc);
  } catch (error) {
    console.error('Error getting seller orders:', error);
    return [];
  }
};

/* Alias */
export const getOrdersBySeller = getSellerOrders;

/* =========================================================
   UPDATE ORDER STATUS (SELLER)
========================================================= */
export const updateOrderStatus = async (
  orderId: string,
  status: Order['status'],
  expectedDeliveryDate?: Date,
  buyerId?: string
) => {
  try {
    const updateData: any = {
      status,
      updatedAt: Timestamp.now(),
    };

    if (expectedDeliveryDate) {
      updateData.expectedDeliveryDate = Timestamp.fromDate(expectedDeliveryDate);
    }

    await updateDoc(doc(db, 'orders', orderId), updateData);

    // Notify buyer
    if (buyerId) {
      let message = '';
      if (status === 'accepted') message = 'Your order has been accepted';
      else if (status === 'shipped') message = 'Your order has been shipped';
      else if (status === 'delivered') message = 'Your order has been delivered';
      else if (status === 'rejected') message = 'Your order has been rejected';

      if (message) {
        await createNotification(
          buyerId,
          'purchase',
          'Order Update',
          message,
          undefined,
          `/orders`
        );
      }
    }
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

/* =========================================================
   GET ORDER BY ID
========================================================= */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
  try {
    const orderDoc = await getDoc(doc(db, 'orders', orderId));
    if (!orderDoc.exists()) return null;
    return mapOrderDoc(orderDoc);
  } catch (error) {
    console.error('Error getting order:', error);
    return null;
  }
};

/* =========================================================
   SHARED MAPPER (IMPORTANT)
========================================================= */
const mapOrderDoc = (docSnap: any): Order => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    expectedDeliveryDate: data.expectedDeliveryDate?.toDate(),
  } as Order;
};
